import React from 'react';
import { Link } from 'react-router';
import {
  BadRequestError,
  CondensedHeader,
  CondensedLarge,
  ToastUtil,
} from '@axiom/ui';
import type { Candidate, Opportunity } from '@axiom/validation';

import { OpportunityTalentApi } from '../api/opportunity-talent-api';
import { CandidateOpportunityLegacyApi } from '../api/candidate-opportunity-legacy-api';

export const AddCandidatesToOpportunityUtil = {
  submit: async ({
    opportunityId,
    opportunityName,
    candidateIds,
    candidateName,
    reloadCandidateId,
  }: {
    opportunityId: Opportunity['id'];
    opportunityName?: string;
    candidateIds: Array<Candidate['id']>;
    candidateName?: string | null;
    reloadCandidateId?: Candidate['id'];
  }): Promise<boolean> => {
    try {
      await OpportunityTalentApi.createOpportunityCandidates({
        opportunityId,
        candidateIds,
      });
      if (reloadCandidateId) {
        await CandidateOpportunityLegacyApi.refreshCandidateOpportunities(
          reloadCandidateId
        );
      }

      const linkCandidateId = candidateIds[0];

      const addedOn = ToastUtil.add({
        dismissible: true,
        name: 'ADD_CANDIDATES_TO_OPPORTUNITY_SUCCESS',
        type: 'info',
        children: (
          <>
            <CondensedHeader>
              {candidateName ?? `${candidateIds.length} Talent`} added to{' '}
              {opportunityName ?? 'Opportunity'}.
            </CondensedHeader>
            {linkCandidateId ? (
              <CondensedLarge>
                <Link
                  data-test="OPEN_OPPORTUNITY_LINK"
                  to={`/opportunity-detail/${opportunityId}/talent/${linkCandidateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    const toast = ToastUtil.getAll().find(
                      t => t.addedOn === addedOn
                    );
                    if (toast) ToastUtil.remove(toast);
                  }}
                >
                  Open opportunity
                </Link>
              </CondensedLarge>
            ) : null}
          </>
        ),
      });

      return true;
    } catch (error) {
      if (error instanceof BadRequestError) {
        const errors = error.getErrors();
        const message =
          (errors as { message?: string } | undefined)?.message ??
          (typeof errors === 'string' ? errors : 'An unknown issue occurred');
        ToastUtil.add({
          dismissible: true,
          name: 'ADD_CANDIDATES_TO_OPPORTUNITY_ERROR',
          type: 'error',
          children: (
            <>
              <CondensedHeader>Error</CondensedHeader>
              <CondensedLarge>{message}</CondensedLarge>
            </>
          ),
        });
        return false;
      }
      throw error;
    }
  },
};
